// src/views/words-lists/components/MoveItemModal.tsx
import React, { FC, useMemo } from 'react';
import { Button, Input, Tree } from 'antd';
import { useTranslation } from 'react-i18next';
import { DataNode } from 'antd/lib/tree';
import { FolderOutlined, HomeOutlined, SearchOutlined } from '@ant-design/icons';
import { TreeNodeType } from '../../../services/ApiService';
import AppModal, { ModalButtons } from '../../../components/AppModal';
import styles from '../FolderTreeView.module.less';

interface MoveItemModalProps {
  visible: boolean;
  itemName: string;
  itemType: 'folder' | 'list';
  folderTree: TreeNodeType[];
  excludedNodeIds: Set<string>;
  onMove: (targetFolderId: number | null) => void;
  onCancel: () => void;
  isMoving?: boolean;
  // „Duplikuj do…” wybiera folder dla kopii w tym samym oknie co „Przenieś”
  action?: 'move' | 'duplicate';
  // Folder zaznaczony na starcie; `null` albo brak to katalog główny
  initialFolderId?: number | null;
}

// Klucz pozycji „Katalog główny” w drzewie; foldery mają klucze liczbowe (id folderu)
const ROOT_KEY = 'root';

// Folder z dziećmi, które przeszły wyszukiwanie
type FolderOption = {
  id: number;
  name: string;
  children: FolderOption[];
};

const toFolderOptions = (nodes: TreeNodeType[], excludedNodeIds: Set<string>): FolderOption[] => nodes
  .filter((node) => node.type === 'folder' && !excludedNodeIds.has(node.id))
  .map((node) => ({
    id: parseInt(node.id.replace('folder-', ''), 10),
    name: node.name,
    children: node.children ? toFolderOptions(node.children, excludedNodeIds) : [],
  }));

// Zostają foldery pasujące do wszystkich wpisanych słów oraz te, w których głębiej jest pasujący folder
const filterFolders = (folders: FolderOption[], searchWords: string[]): FolderOption[] => folders
  .reduce<FolderOption[]>((result, folder) => {
  const children = filterFolders(folder.children, searchWords);
  const name = folder.name.toLowerCase();
  const matches = searchWords.every((word) => name.includes(word));

  return matches || children.length > 0 ? [...result, { ...folder, children }] : result;
}, []);

const collectIds = (folders: FolderOption[]): number[] => folders
  .flatMap((folder) => [folder.id, ...collectIds(folder.children)]);

const toTreeData = (folders: FolderOption[]): DataNode[] => folders.map((folder) => ({
  key: folder.id,
  title: folder.name,
  icon: <FolderOutlined />,
  children: folder.children.length > 0 ? toTreeData(folder.children) : undefined,
}));

export const MoveItemModal: FC<MoveItemModalProps> = ({
  visible,
  itemName,
  itemType,
  folderTree,
  excludedNodeIds,
  onMove,
  onCancel,
  isMoving = false,
  action = 'move',
  initialFolderId = null,
}) => {
  const { t } = useTranslation();
  const [searchValue, setSearchValue] = React.useState('');
  const [selectedFolderId, setSelectedFolderId] = React.useState<number | undefined>(initialFolderId ?? undefined);

  const folders = useMemo(() => {
    const searchWords = searchValue.toLowerCase().split(/\s+/).filter(Boolean);
    const allFolders = toFolderOptions(folderTree, excludedNodeIds);

    return searchWords.length > 0 ? filterFolders(allFolders, searchWords) : allFolders;
  }, [folderTree, excludedNodeIds, searchValue]);

  // Na starcie drzewo rozwinięte w całości; każde wyszukiwanie rozwija od nowa wszystko, co po nim zostało
  const [expandedKeys, setExpandedKeys] = React.useState<React.Key[]>(() => [ROOT_KEY, ...collectIds(folders)]);

  React.useEffect(() => {
    setExpandedKeys([ROOT_KEY, ...collectIds(folders)]);
  }, [folders]);

  // Foldery leżą pod „Katalogiem głównym” — wybór katalogu głównego to zwykłe kliknięcie, a nie puste pole
  const treeData = useMemo<DataNode[]>(() => [{
    key: ROOT_KEY,
    title: t('folders:move.root'),
    icon: <HomeOutlined />,
    children: toTreeData(folders),
  }], [folders, t]);

  // Bez czyszczenia wyboru: przy duplikowaniu okno czeka otwarte na serwer, a po błędzie ponowna próba
  // ma iść do tego samego folderu. Wybór czyści zamknięcie okna (efekt niżej)
  const handleOk = () => {
    onMove(selectedFolderId || null);
  };

  const handleCancel = () => {
    setSelectedFolderId(undefined);
    setSearchValue('');
    onCancel();
  };

  React.useEffect(() => {
    if (!visible) {
      setSelectedFolderId(undefined);
      setSearchValue('');
    }
  }, [visible]);

  const isDuplicate = action === 'duplicate';
  let title = isDuplicate ? t('folders:move.duplicateList') : t('folders:move.moveList');

  if (itemType === 'folder') {
    title = isDuplicate ? t('folders:move.duplicateFolder') : t('folders:move.moveFolder');
  }

  return (
    <AppModal
      title={title}
      visible={visible}
      onCancel={handleCancel}
      className={styles.moveModal}
    >
      <div className={styles.moveModalBody}>
        <div className={styles.moveModalItemName}>{itemName}</div>
        <p>
          {isDuplicate ? t('folders:move.chooseForCopy') : t('folders:move.chooseTarget')}
        </p>
        <Input
          className={styles.moveModalSearch}
          prefix={<SearchOutlined />}
          placeholder={t('folders:move.search')}
          allowClear
          value={searchValue}
          onChange={(event) => setSearchValue(event.target.value)}
        />
        <div className={styles.moveModalTree}>
          <Tree
            showIcon
            blockNode
            treeData={treeData}
            expandedKeys={expandedKeys}
            onExpand={setExpandedKeys}
            selectedKeys={[selectedFolderId ?? ROOT_KEY]}
            // Z węzła, nie z listy zaznaczonych: antd odznacza ponownie kliknięty folder, a tu coś zawsze jest wybrane
            onSelect={(_, { node }) => setSelectedFolderId(node.key === ROOT_KEY ? undefined : Number(node.key))}
          />
        </div>
        <ModalButtons>
          <Button onClick={handleCancel}>
            {t('common:buttons.cancel')}
          </Button>
          <Button type="primary" onClick={handleOk} loading={isMoving}>
            {isDuplicate ? t('folders:move.duplicate') : t('folders:move.move')}
          </Button>
        </ModalButtons>
      </div>
    </AppModal>
  );
};
